/**
 * Viewer entry point
 * 
 * This module exports everything needed by the Component Explorer viewer.
 * Projects should NOT import from this module - use '@vscode/component-explorer' instead.
 */

export * from './core/index.js';

export type {
  FixtureExport,
  SingleFixtureExport,
  FixtureGroupExport,
  FixtureVariantsExport,
} from './core/fixtureApi.js';

import { type FixtureModules, type ExplorerRuntime } from './core/index.js';
import { ExplorerUIMode } from './modes/ExplorerUIMode.js';
import { HeadlessMode } from './modes/HeadlessMode.js';
import { EmbeddedMode } from './modes/EmbeddedMode.js';
import { parseExplorerQueryParams, type ExplorerQueryParams } from './lib/explorerQueryParams.js';
import type { DaemonConfig } from './daemon/DaemonConfig.js';

export type { ExplorerMode, ExplorerQueryParams } from './lib/explorerQueryParams.js';

// Main exports
export { Explorer, ExplorerModel } from './components/index.js';
export type { ExplorerTreeNode } from './components/index.js';
export { toExplorerTree } from './components/index.js';

// Sub-components
export {
  TitleBar,
  TitleBarButton,
  LeftSidebar,
  RightSidebar,
  TreeView,
  TreeItem,
} from './components/index.js';

// Icons
export * from './components/icons.js';

// Styles utilities
export { styles, mergeStyles } from './components/index.js';

// Daemon exports
export { DaemonModel } from './daemon/index.js';
export type { DaemonScreenshotResult, DaemonCompareResult, DaemonFixtureInfo } from './daemon/DaemonModel.js';

// Report exports
export { ReportModel } from './report/index.js';
export type { ScreenshotReport, ReportFixtureEntry, ReportSummary, FixtureStatus } from './report/index.js';

export interface ExplorerAppOptions {
  readonly loadFixtures: (filter?: { file: string }) => Promise<FixtureModules>;
  readonly daemonConfig?: DaemonConfig;
}

export class ExplorerApp implements ExplorerRuntime {
  private readonly _runtime: ExplorerRuntime;

  constructor(element: HTMLElement, options: ExplorerAppOptions) {
    const buildInfo = globalThis._buildInfo;
    if (buildInfo) {
      console.log(`[component-explorer] v${buildInfo.version} (built ${buildInfo.date})`);
    }

    const params = new URLSearchParams(location.search);
    const query: ExplorerQueryParams = parseExplorerQueryParams(params);
    const fixtureId = query.fixture;

    if (query.mode === 'embedded') {
      if (!fixtureId) {
        throw new Error('Embedded mode requires a fixture query parameter');
      }
      const file = query.file;
      const fixtures = options.loadFixtures(file ? { file } : undefined);
      this._runtime = new EmbeddedMode(element, fixtureId, fixtures);
    } else {
      const runtime = query.mode === 'headless'
        ? new HeadlessMode(element, {})
        : new ExplorerUIMode(element, {}, true, options.daemonConfig);
      this._runtime = runtime;
      options.loadFixtures().then(m => this._runtime.updateFixtures(m));
    }
  }

  updateFixtures(fixtureModules: FixtureModules): void {
    this._runtime.updateFixtures(fixtureModules);
  }

  bumpVersion(): void {
    this._runtime.bumpVersion();
  }
}
